import React from 'react';
import { motion } from 'framer-motion';
import { useLocation, Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, Share2, Linkedin, Twitter, Mail } from 'lucide-react';
export const InsightDetail = () => {
  const location = useLocation();
  const {
    slug
  } = useParams();
  // Try to get post from state, or use a fallback if navigated directly
  const post = location.state?.post || {
    title: slug ? slug.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : 'Strategic Insight',
    category: 'Advisory',
    date: 'July 2024',
    author: 'Managing Partner',
    img: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&q=80',
    excerpt: 'Navigating the complexities of modern business requires a proactive approach and deep technical mastery.'
  };
  if (!post) {
    return <Navigate to="/insights" data-id="element-354" />;
  }
  return <main className="pt-20 bg-brand-warm-white min-h-screen" data-id="element-355">
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[500px] flex items-end pb-20" data-id="element-356">
        <div className="absolute inset-0 z-0" data-id="element-357">
          <img src={post.img} alt={post.title} className="w-full h-full object-cover" data-id="element-358" />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-navy via-brand-navy/60 to-transparent" data-id="element-359" />
        </div>

        <div className="max-w-4xl mx-auto px-6 relative z-10 w-full" data-id="element-360">
          <Link to="/insights" className="inline-flex items-center gap-2 text-white/70 hover:text-brand-gold transition-colors mb-8 text-sm font-bold tracking-widest uppercase" data-id="element-361">
            <ArrowLeft size={16} data-id="element-362" /> Back to Insights
          </Link>

          <div className="flex items-center gap-4 mb-6" data-id="element-363">
            <span className="bg-brand-gold text-white px-4 py-1 text-[10px] font-bold tracking-widest uppercase rounded-full" data-id="element-364">
              {post.category}
            </span>
            <div className="text-white/60 text-xs uppercase tracking-widest flex items-center gap-3" data-id="element-365">
              <span data-id="element-366">{post.date}</span>
              <span className="w-1 h-1 rounded-full bg-brand-gold" data-id="element-367" />
              <span data-id="element-368">{post.author}</span>
            </div>
          </div>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-4xl md:text-6xl font-display text-white leading-tight mb-6" data-id="element-369">
            {post.title}
          </motion.h1>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-24" data-id="element-370">
        <div className="max-w-4xl mx-auto px-6 flex flex-col lg:flex-row gap-16" data-id="element-371">
          {/* Social Share Sidebar */}
          <div className="lg:w-24 flex-shrink-0" data-id="element-372">
            <div className="sticky top-32 flex lg:flex-col gap-6 items-center" data-id="element-373">
              <span className="text-brand-charcoal/40 text-xs font-bold uppercase tracking-widest lg:-rotate-90 lg:mb-8 whitespace-nowrap" data-id="element-374">
                Share
              </span>
              <button className="w-10 h-10 rounded-full border border-brand-navy/10 flex items-center justify-center text-brand-navy hover:bg-brand-navy hover:text-white transition-all" data-id="element-375">
                <Linkedin size={18} data-id="element-376" />
              </button>
              <button className="w-10 h-10 rounded-full border border-brand-navy/10 flex items-center justify-center text-brand-navy hover:bg-brand-navy hover:text-white transition-all" data-id="element-377">
                <Twitter size={18} data-id="element-378" />
              </button>
              <button className="w-10 h-10 rounded-full border border-brand-navy/10 flex items-center justify-center text-brand-navy hover:bg-brand-navy hover:text-white transition-all" data-id="element-379">
                <Mail size={18} data-id="element-380" />
              </button>
            </div>
          </div>

          {/* Article Body */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="prose prose-lg prose-brand max-w-none" data-id="element-381">
            <p className="text-2xl text-brand-navy font-display leading-relaxed mb-12" data-id="element-382">
              {post.excerpt || 'In an increasingly interconnected global economy, strategic foresight and rigorous financial planning are no longer optional—they are imperative for sustainable growth and risk mitigation.'}
            </p>

            <h3 className="text-3xl font-display text-brand-navy mt-12 mb-6" data-id="element-383">
              The Current Landscape
            </h3>
            <p className="text-brand-charcoal/80 leading-relaxed mb-8" data-id="element-384">
              Organizations today face a unique confluence of challenges:
              shifting regulatory frameworks, rapid technological disruption,
              and volatile market dynamics. Navigating this environment requires
              a multidisciplinary approach that integrates tax optimization,
              capital structuring, and operational strategy.
            </p>
            <p className="text-brand-charcoal/80 leading-relaxed mb-8" data-id="element-385">
              Our recent analysis indicates that companies proactively adapting
              their cross-border strategies are significantly outperforming
              their peers in both capital efficiency and market share
              acquisition.
            </p>

            <div className="my-16 p-10 bg-brand-navy text-white rounded-2xl relative overflow-hidden" data-id="element-386">
              <div className="absolute top-0 right-0 w-32 h-32 bg-brand-gold/20 rounded-full blur-[40px] -mr-16 -mt-16" data-id="element-387" />
              <p className="text-2xl font-display italic relative z-10" data-id="element-388">
                "Success in modern markets is defined not just by the ability to
                identify opportunities, but by the structural agility to execute
                on them efficiently."
              </p>
            </div>

            <h3 className="text-3xl font-display text-brand-navy mt-12 mb-6" data-id="element-389">
              Strategic Imperatives
            </h3>
            <ul className="space-y-4 mb-8 text-brand-charcoal/80" data-id="element-390">
              <li className="flex items-start gap-4" data-id="element-391">
                <div className="mt-2 w-1.5 h-1.5 rounded-full bg-brand-gold flex-shrink-0" data-id="element-392" />
                <span data-id="element-393">
                  <strong data-id="element-394">Comprehensive Risk Assessment:</strong> Evaluating
                  exposure across multiple jurisdictions and regulatory regimes.
                </span>
              </li>
              <li className="flex items-start gap-4" data-id="element-395">
                <div className="mt-2 w-1.5 h-1.5 rounded-full bg-brand-gold flex-shrink-0" data-id="element-396" />
                <span data-id="element-397">
                  <strong data-id="element-398">Capital Structure Optimization:</strong> Aligning debt
                  and equity profiles with long-term strategic objectives.
                </span>
              </li>
              <li className="flex items-start gap-4" data-id="element-399">
                <div className="mt-2 w-1.5 h-1.5 rounded-full bg-brand-gold flex-shrink-0" data-id="element-400" />
                <span data-id="element-401">
                  <strong data-id="element-402">Operational Agility:</strong> Building flexible
                  frameworks that can adapt to sudden market shifts.
                </span>
              </li>
            </ul>

            <p className="text-brand-charcoal/80 leading-relaxed mb-8" data-id="element-403">
              As we look to the future, the integration of these elements will
              separate market leaders from the rest. Consilium Advisory remains
              committed to providing the insights and execution capabilities
              necessary to navigate this complex terrain.
            </p>
          </motion.div>
        </div>
      </section>
    </main>;
};